/*
Copyright (c) 2024, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/

import * as React from 'react';
import useEffect from './use_effect';

export default function useMediaQuery(
  query: string,
  { defaultMatch = false } = {}
): boolean {
  const supportsMatchMedia =
    typeof window !== 'undefined' && typeof window.matchMedia !== 'undefined';

  const matchMedia = supportsMatchMedia ? window.matchMedia : null;

  const [match, setMatch] = React.useState(defaultMatch);

  useEffect(() => {
    if (!supportsMatchMedia || !matchMedia) {
      return undefined;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mql: any = matchMedia(query);

    const supportsEventListener =
      'addEventListener' in mql && 'removeEventListener' in mql;

    setMatch(mql.matches);

    const handleQueryChange = (e: MediaQueryListEvent) => {
      setMatch(e.matches);
    };

    if (supportsEventListener) {
      mql.addEventListener('change', handleQueryChange);
    } else {
      mql.addListener(handleQueryChange);
    }
    return () => {
      if (supportsEventListener) {
        mql.removeEventListener('change', handleQueryChange);
      } else {
        mql.removeListener(handleQueryChange);
      }
    };
  }, [matchMedia, query, supportsMatchMedia]);

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useDebugValue({ match, query });
  }

  return match;
}
